import React, { useCallback, useRef, useState } from 'react';
import { useSceneStore } from '../../store/sceneStore';
import { useSceneViewStore } from '../../store/sceneViewStore';
import { GameObject, Transform } from '../../../core/types/gameobject.types';

/** Walk up parent chain to compute absolute world position */
function getWorldPosition(
  gameObject: GameObject,
  gameObjects: Record<string, GameObject>
): { x: number; y: number } {
  let x = gameObject.transform.position.x;
  let y = gameObject.transform.position.y;
  let current = gameObject;
  while (current.parentId) {
    const parent = gameObjects[current.parentId];
    if (!parent) break;
    x += parent.transform.position.x;
    y += parent.transform.position.y;
    current = parent;
  }
  return { x, y };
}

interface TransformGizmoProps {
  gameObject: GameObject;
  canvasWidth: number;
  canvasHeight: number;
}

type GizmoAxis = 'x' | 'y' | 'center' | 'rotation' | null;

export const TransformGizmo: React.FC<TransformGizmoProps> = ({
  gameObject,
  canvasWidth,
  canvasHeight,
}) => {
  const { updateTransform, updateMultipleTransforms, selectedIds, gameObjects } = useSceneStore();
  const { viewport, activeTool, setIsTransforming } = useSceneViewStore();
  const { zoom, panX, panY, showGizmos } = viewport;

  const [activeAxis, setActiveAxis] = useState<GizmoAxis>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{
    x: number;
    y: number;
    transform: Transform;
    allSelectedTransforms: Record<string, Transform>;
  } | null>(null);

  // Calculate screen position to match GameObjectRenderer exactly
  const { transform } = gameObject;

  // Use world position so children follow parent transforms
  const worldPos = gameObject.parentId
    ? getWorldPosition(gameObject, gameObjects)
    : transform.position;

  // Screen position of the object's center (same calculation as GameObjectRenderer)
  const screenX = (canvasWidth / 2) + (worldPos.x + panX) * zoom;
  const screenY = (canvasHeight / 2) + (worldPos.y + panY) * zoom;

  // Gizmo configuration
  const gizmoSize = 80;
  const handleSize = 12;
  const rotationRingRadius = gizmoSize + 10;

  // IMPORTANT: All hooks must be called before any conditional returns
  // to satisfy React's Rules of Hooks
  const handleMouseDown = useCallback(
    (axis: GizmoAxis) => (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      setActiveAxis(axis);
      setIsDragging(true);
      setIsTransforming(true);

      // Pause undo history so the entire drag is one undo entry
      useSceneStore.temporal.getState().pause();

      // Capture initial transforms for all selected objects
      const allSelectedTransforms: Record<string, Transform> = {};
      selectedIds.forEach((id) => {
        const obj = gameObjects[id];
        if (obj) {
          allSelectedTransforms[id] = { ...obj.transform };
        }
      });

      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        transform: { ...transform },
        allSelectedTransforms,
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!dragStartRef.current) return;

        const deltaX = (moveEvent.clientX - dragStartRef.current.x) / zoom;
        const deltaY = (moveEvent.clientY - dragStartRef.current.y) / zoom;
        const startTransform = dragStartRef.current.transform;

        if (activeTool === 'move') {
          // Calculate the movement delta based on axis constraints
          let moveDeltaX = 0;
          let moveDeltaY = 0;

          if (axis === 'x' || axis === 'center') {
            moveDeltaX = deltaX;
          }
          if (axis === 'y' || axis === 'center') {
            moveDeltaY = deltaY;
          }

          // Apply movement to all selected objects
          if (selectedIds.length > 1 && selectedIds.includes(gameObject.id)) {
            // Multi-select: update all selected objects from their initial positions
            selectedIds.forEach((id) => {
              const initialTransform = dragStartRef.current?.allSelectedTransforms[id];
              if (initialTransform) {
                const newPosition = {
                  x: initialTransform.position.x + moveDeltaX,
                  y: initialTransform.position.y + moveDeltaY,
                };
                updateTransform(id, { position: newPosition });
              }
            });
          } else {
            // Single selection: update only the dragged object
            const newPosition = {
              x: startTransform.position.x + moveDeltaX,
              y: startTransform.position.y + moveDeltaY,
            };
            updateTransform(gameObject.id, { position: newPosition });
          }
        } else if (activeTool === 'scale') {
          const scaleFactor = 0.01;

          // Calculate scale delta based on axis
          let scaleDeltaX = 0;
          let scaleDeltaY = 0;

          if (axis === 'x' || axis === 'center') {
            scaleDeltaX = deltaX * scaleFactor;
          }
          if (axis === 'y' || axis === 'center') {
            scaleDeltaY = -deltaY * scaleFactor; // Negative because Y is inverted
          }

          // Apply scale to all selected objects
          if (selectedIds.length > 1 && selectedIds.includes(gameObject.id)) {
            selectedIds.forEach((id) => {
              const initialTransform = dragStartRef.current?.allSelectedTransforms[id];
              if (initialTransform) {
                const newScale = {
                  x: Math.max(0.1, initialTransform.scale.x + scaleDeltaX),
                  y: Math.max(0.1, initialTransform.scale.y + scaleDeltaY),
                };
                updateTransform(id, { scale: newScale });
              }
            });
          } else {
            const newScale = {
              x: Math.max(0.1, startTransform.scale.x + scaleDeltaX),
              y: Math.max(0.1, startTransform.scale.y + scaleDeltaY),
            };
            updateTransform(gameObject.id, { scale: newScale });
          }
        } else if (activeTool === 'rotate' || axis === 'rotation') {
          // Calculate rotation angle based on mouse position relative to object center
          const centerX = screenX;
          const centerY = screenY;

          const startAngle = Math.atan2(
            dragStartRef.current.y - centerY,
            dragStartRef.current.x - centerX
          );
          const currentAngle = Math.atan2(
            moveEvent.clientY - centerY,
            moveEvent.clientX - centerX
          );

          const angleDelta = (currentAngle - startAngle) * (180 / Math.PI);

          // Apply rotation to all selected objects
          if (selectedIds.length > 1 && selectedIds.includes(gameObject.id)) {
            selectedIds.forEach((id) => {
              const initialTransform = dragStartRef.current?.allSelectedTransforms[id];
              if (initialTransform) {
                const newRotation = initialTransform.rotation + angleDelta;
                updateTransform(id, { rotation: newRotation });
              }
            });
          } else {
            const newRotation = startTransform.rotation + angleDelta;
            updateTransform(gameObject.id, { rotation: newRotation });
          }
        }
      };

      const handleMouseUp = () => {
        // Batch entire drag as one undo entry:
        // 1. Capture final transforms (current state after drag)
        const finalGameObjects = { ...useSceneStore.getState().gameObjects };

        // 2. Restore pre-drag state while still paused (not recorded)
        if (dragStartRef.current) {
          const restoredGameObjects = { ...finalGameObjects };
          Object.entries(dragStartRef.current.allSelectedTransforms).forEach(([id, initialTransform]) => {
            if (restoredGameObjects[id]) {
              restoredGameObjects[id] = { ...restoredGameObjects[id], transform: initialTransform };
            }
          });
          useSceneStore.setState({ gameObjects: restoredGameObjects });
        }

        // 3. Resume temporal — tracking is back on
        useSceneStore.temporal.getState().resume();

        // 4. Apply final transforms — this single set() records pre-drag → post-drag
        useSceneStore.setState({ gameObjects: finalGameObjects });

        setActiveAxis(null);
        setIsDragging(false);
        setIsTransforming(false);
        dragStartRef.current = null;

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    },
    [
      transform,
      zoom,
      activeTool,
      gameObject.id,
      updateTransform,
      setIsTransforming,
      screenX,
      screenY,
      selectedIds,
      gameObjects,
    ]
  );

  // Early return for cases where gizmo should not render
  // This must come AFTER all hooks to satisfy React's Rules of Hooks
  if (!showGizmos || activeTool === 'pan') {
    return null;
  }

  // Render gizmo based on active tool
  if (activeTool === 'select') {
    // Simple selection indicator
    return (
      <div
        className="absolute pointer-events-none"
        style={{
          left: screenX - 20,
          top: screenY - 20,
          width: 40,
          height: 40,
        }}
      >
        <div className="w-full h-full border-2 border-blue-500 rounded-sm opacity-70" />
      </div>
    );
  }

  if (activeTool === 'move') {
    const svgSize = gizmoSize * 2 + 20;
    return (
      <div
        className="absolute pointer-events-none"
        style={{
          left: screenX - svgSize / 2,
          top: screenY - svgSize / 2,
          width: svgSize,
          height: svgSize,
        }}
      >
        <svg
          width={svgSize}
          height={svgSize}
          className="pointer-events-none"
        >
          <g transform={`translate(${svgSize / 2}, ${svgSize / 2})`}>
            {/* X Axis (Red) */}
            <line
              x1={0}
              y1={0}
              x2={gizmoSize}
              y2={0}
              stroke={activeAxis === 'x' ? '#ff6b6b' : '#ef4444'}
              strokeWidth={activeAxis === 'x' ? 3 : 2}
              className="pointer-events-auto cursor-e-resize"
              onMouseDown={handleMouseDown('x')}
            />
            <polygon
              points={`${gizmoSize - 5},-5 ${gizmoSize + 10},0 ${gizmoSize - 5},5`}
              fill={activeAxis === 'x' ? '#ff6b6b' : '#ef4444'}
              className="pointer-events-auto cursor-e-resize"
              onMouseDown={handleMouseDown('x')}
            />

            {/* Y Axis (Green) */}
            <line
              x1={0}
              y1={0}
              x2={0}
              y2={-gizmoSize}
              stroke={activeAxis === 'y' ? '#6bff6b' : '#22c55e'}
              strokeWidth={activeAxis === 'y' ? 3 : 2}
              className="pointer-events-auto cursor-n-resize"
              onMouseDown={handleMouseDown('y')}
            />
            <polygon
              points={`-5,${-gizmoSize + 5} 0,${-gizmoSize - 10} 5,${-gizmoSize + 5}`}
              fill={activeAxis === 'y' ? '#6bff6b' : '#22c55e'}
              className="pointer-events-auto cursor-n-resize"
              onMouseDown={handleMouseDown('y')}
            />

            {/* Center Handle (Yellow) */}
            <rect
              x={-handleSize / 2}
              y={-handleSize / 2}
              width={handleSize}
              height={handleSize}
              fill={activeAxis === 'center' ? '#ffeb3b' : '#eab308'}
              stroke="rgba(0,0,0,0.3)"
              strokeWidth={1}
              className="pointer-events-auto cursor-move"
              onMouseDown={handleMouseDown('center')}
            />
          </g>
        </svg>
      </div>
    );
  }

  if (activeTool === 'scale') {
    const svgSize = gizmoSize * 2 + 20;
    return (
      <div
        className="absolute pointer-events-none"
        style={{
          left: screenX - svgSize / 2,
          top: screenY - svgSize / 2,
          width: svgSize,
          height: svgSize,
        }}
      >
        <svg
          width={svgSize}
          height={svgSize}
          className="pointer-events-none"
        >
          <g transform={`translate(${svgSize / 2}, ${svgSize / 2})`}>
            {/* X Axis (Red) */}
            <line
              x1={0}
              y1={0}
              x2={gizmoSize}
              y2={0}
              stroke={activeAxis === 'x' ? '#ff6b6b' : '#ef4444'}
              strokeWidth={activeAxis === 'x' ? 3 : 2}
              className="pointer-events-auto cursor-e-resize"
              onMouseDown={handleMouseDown('x')}
            />
            <rect
              x={gizmoSize - handleSize / 2}
              y={-handleSize / 2}
              width={handleSize}
              height={handleSize}
              fill={activeAxis === 'x' ? '#ff6b6b' : '#ef4444'}
              className="pointer-events-auto cursor-e-resize"
              onMouseDown={handleMouseDown('x')}
            />

            {/* Y Axis (Green) */}
            <line
              x1={0}
              y1={0}
              x2={0}
              y2={-gizmoSize}
              stroke={activeAxis === 'y' ? '#6bff6b' : '#22c55e'}
              strokeWidth={activeAxis === 'y' ? 3 : 2}
              className="pointer-events-auto cursor-n-resize"
              onMouseDown={handleMouseDown('y')}
            />
            <rect
              x={-handleSize / 2}
              y={-gizmoSize - handleSize / 2}
              width={handleSize}
              height={handleSize}
              fill={activeAxis === 'y' ? '#6bff6b' : '#22c55e'}
              className="pointer-events-auto cursor-n-resize"
              onMouseDown={handleMouseDown('y')}
            />

            {/* Center Handle (Yellow) - Uniform Scale */}
            <rect
              x={-handleSize / 2}
              y={-handleSize / 2}
              width={handleSize}
              height={handleSize}
              fill={activeAxis === 'center' ? '#ffeb3b' : '#eab308'}
              stroke="rgba(0,0,0,0.3)"
              strokeWidth={1}
              className="pointer-events-auto cursor-nwse-resize"
              onMouseDown={handleMouseDown('center')}
            />
          </g>
        </svg>
      </div>
    );
  }

  if (activeTool === 'rotate') {
    const svgSize = rotationRingRadius * 2 + 20;
    return (
      <div
        className="absolute pointer-events-none"
        style={{
          left: screenX - svgSize / 2,
          top: screenY - svgSize / 2,
          width: svgSize,
          height: svgSize,
        }}
      >
        <svg
          width={svgSize}
          height={svgSize}
          className="pointer-events-none"
        >
          <g transform={`translate(${svgSize / 2}, ${svgSize / 2})`}>
            {/* Rotation Ring */}
            <circle
              cx={0}
              cy={0}
              r={rotationRingRadius}
              fill="none"
              stroke={activeAxis === 'rotation' ? '#60a5fa' : 'rgba(59, 130, 246, 0.6)'}
              strokeWidth={activeAxis === 'rotation' ? 6 : 4}
              strokeDasharray="10 5"
              className="pointer-events-auto cursor-grab"
              style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
              onMouseDown={handleMouseDown('rotation')}
            />

            {/* Rotation indicator line */}
            <line
              x1={0}
              y1={0}
              x2={0}
              y2={-rotationRingRadius}
              stroke="rgba(59, 130, 246, 0.8)"
              strokeWidth={2}
              transform={`rotate(${transform.rotation})`}
            />

            {/* Center point */}
            <circle
              cx={0}
              cy={0}
              r={4}
              fill="rgba(59, 130, 246, 0.8)"
            />

            {/* Rotation angle display */}
            {activeAxis === 'rotation' && (
              <text
                x={0}
                y={rotationRingRadius + 20}
                textAnchor="middle"
                fill="white"
                fontSize={12}
                className="select-none"
              >
                {Math.round(transform.rotation)}
              </text>
            )}
          </g>
        </svg>
      </div>
    );
  }

  return null;
};
